import algoliasearch from 'algoliasearch'
import states from '@config/states'

const MAX_NUM_RESULTS = 200
const METERS_PER_MILE = 1609.344
const GOLF_FACILITY_FILTER = 'object_type:Facility AND facility_type_label:"Golf Facility"'

let _index

function getFacilitiesIndex() {
  if (!_index) {
    const appId = process.env.ALGOLIA_APP_ID
    const apiKey = process.env.ALGOLIA_API_KEY
    const indexName = process.env.ALGOLIA_FACILITIES_INDEX_NAME || 'MemberFacilityDirectory'
    if (!appId || !apiKey) {
      throw new Error('ALGOLIA_APP_ID and ALGOLIA_API_KEY must be set for facility search')
    }
    _index = algoliasearch(appId, apiKey).initIndex(indexName)
  }
  return _index
}

/** Escape a value embedded in an Algolia filter string literal. */
export function escapeAlgoliaFilterValue(value) {
  return String(value ?? '').replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

function stateCodeFromHit(hit) {
  const raw = hit?.state ?? hit?.state_code ?? hit?.stateCode
  if (raw) {
    const upper = String(raw).trim().toUpperCase()
    if (/^[A-Z]{2}$/.test(upper)) return upper
  }
  const stateName = hit?.state_name ?? hit?.stateName
  if (!stateName) return null
  const entry = Object.entries(states).find(
    ([, name]) => name.toLowerCase() === String(stateName).trim().toLowerCase(),
  )
  return entry?.[0] ?? null
}

/**
 * Map a MemberFacilityDirectory hit to the CourseSearchResults / CardCourse / Link shape.
 */
export function mapAlgoliaFacilityHit(hit) {
  if (!hit || typeof hit !== 'object') return null

  const facilityId = hit.facility_id ?? hit.facilityId ?? hit.objectID
  if (facilityId == null || facilityId === '') return null

  const name = hit.name ?? hit.facility_name ?? hit.facilityName ?? null
  const photo = hit.photo_facility ?? hit.photoFacility ?? null
  const address1 = hit.address1 ?? hit.address_1 ?? hit.street ?? hit.address ?? null

  return {
    facility_id: String(facilityId),
    name,
    city: hit.city ?? null,
    state: stateCodeFromHit(hit),
    zip: hit.zip ?? hit.postal_code ?? hit.postalCode ?? null,
    address1,
    photo_facility: photo,
    // CardCourse reads camelCase photoFacility from the spread props.
    photoFacility: photo,
    __typename: 'Facility',
  }
}

function mapAndSortHits(hits) {
  if (!Array.isArray(hits) || hits.length === 0) return []
  return hits
    .map(mapAlgoliaFacilityHit)
    .filter(Boolean)
    .sort((a, b) => ((a.name > b.name) ? 1 : -1))
}

function radiusMeters(miles) {
  return Math.ceil(miles * METERS_PER_MILE)
}

/** Mirrors pga-algolia-service Facilities.findByCityState. */
export async function searchFacilitiesByCityState(stateName, city) {
  const index = getFacilitiesIndex()
  const result = await index.search('', {
    filters: `${GOLF_FACILITY_FILTER} AND state_name:"${escapeAlgoliaFilterValue(stateName)}" AND city:"${escapeAlgoliaFilterValue(city)}"`,
    hitsPerPage: MAX_NUM_RESULTS,
  })
  return mapAndSortHits(result.hits)
}

/** Mirrors pga-algolia-service Facilities.searchCourses. */
export async function searchFacilitiesByText(searchText) {
  const index = getFacilitiesIndex()
  const result = await index.search(String(searchText), {
    restrictSearchableAttributes: [
      'facility_name',
      'name',
      'zip',
      'city',
      'state_name',
    ],
    filters: GOLF_FACILITY_FILTER,
    hitsPerPage: MAX_NUM_RESULTS,
  })
  return mapAndSortHits(result.hits)
}

/** Mirrors pga-algolia-service Facilities.searchCoursesByZipCode radius leg. */
export async function searchFacilitiesByRadius(latitude, longitude, radiusMiles) {
  const index = getFacilitiesIndex()
  const result = await index.search('', {
    aroundLatLng: `${latitude}, ${longitude}`,
    aroundRadius: radiusMeters(radiusMiles),
    filters: GOLF_FACILITY_FILTER,
    hitsPerPage: MAX_NUM_RESULTS,
  })
  return mapAndSortHits(result.hits)
}

/** Test hook to reset cached Algolia index between tests. */
export function resetFacilitiesIndexForTests() {
  _index = undefined
}
